import { AuthSwitchLink } from "@/components/auth/AuthSwitchLink.web";
import { AuthTermsNotice } from "@/components/auth/AuthTermsNotice.web";
import { OrDivider } from "@/components/auth/OrDivider.web";
import { SocialSignInRow } from "@/components/auth/SocialSignInRow.web";
import { ThemedText } from "@/components/ThemedText";
import AlertModal, { type AlertAction } from "@/components/ui/AlertModal.web";
import {
  FormButton,
  FormCard,
  FormLinkButton,
  FormPageContainer,
  RHFFormInput,
} from "@/components/ui/forms";
import {
  PhoneAuthFlow,
  type PhoneAuthSuccessPayload,
} from "@/components/ui/PhoneAuthFlow.web";
import { auth, firestore } from "@/firebase/web";
import { ensureUserDocExists } from "@/utils/createUserDoc.web";
import type { PhoneVerificationErrorCode } from "@/utils/phoneVerification.web";
import { clearSignupParams } from "@/utils/signupParams.web";
import { zodResolver } from "@hookform/resolvers/zod";
import { Image } from "expo-image";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { StatusBar } from "expo-status-bar";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { getErrorCode, mapAuthErrorCode } from "../../utils/errors";
import { loginSchema, type LoginFormValues } from "./login.schema";

// Resolves with the signed-in user once auth state settles, or null after the
// timeout. Handles the COOP race where signInWithPopup throws even though the
// sign-in succeeded but auth.currentUser hasn't propagated yet.
const waitForCurrentUser = (timeoutMs: number): Promise<User | null> => {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }
    let done = false;
    const unsub = onAuthStateChanged(auth, (u) => {
      if (done || !u) return;
      done = true;
      clearTimeout(timer);
      unsub();
      resolve(u);
    });
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      unsub();
      resolve(auth.currentUser);
    }, timeoutMs);
  });
};

const LoginScreen = () => {
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 768;
  const { t } = useTranslation();

  const [authLoading, setAuthLoading] = useState(false);
  const [phoneFlowActive, setPhoneFlowActive] = useState(false);
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
    onClose?: () => void;
    primaryAction?: AlertAction;
    secondaryAction?: AlertAction;
  } | null>(null);
  const router = useRouter();

  // Signup-URL params (phoneNumberMethod, groupId) are tied to signup intent.
  // Anyone in the login flow should not have a leftover entry consumed when
  // they finish signing in — clear once on mount so the post-onboarding
  // handoff in _layout.web.tsx finds nothing to act on.
  useEffect(() => {
    clearSignupParams();
  }, []);

  const { control, handleSubmit } = useForm<LoginFormValues>({
    mode: "onTouched",
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const watchedFields = useWatch({ control, name: ["email", "password"] });
  const [watchedEmail, watchedPassword] = watchedFields ?? [];
  const hasRequiredFields = !!watchedEmail && !!watchedPassword;

  const handlePhoneSignInSendError = ({
    defaultMessageKey,
  }: {
    code: PhoneVerificationErrorCode;
    defaultMessageKey: string;
  }) => {
    setModalInfo({ title: t("ERROR"), message: t(defaultMessageKey) });
  };

  const handlePhoneSignInConfirm = async ({
    userCredential,
    phonePrefix,
    phoneNumber,
  }: PhoneAuthSuccessPayload) => {
    if (!userCredential) return;
    const signedInUser = userCredential.user;
    setPhoneFlowActive(false);

    // First-time phone user (no doc): helper writes the default shape and we
    // route to onboarding. Returning phone user: layout routes by their
    // existing onboarding state (isPhoneOnlyUser + hasFollowedPlate live in
    // app/_layout.web.tsx, which is the right place for that decision since
    // it varies by route).
    const { created } = await ensureUserDocExists(firestore, signedInUser, {
      source: "login",
    });

    // Firebase's signInWithPhoneNumber creates a new Auth account for a phone
    // it has never seen — a user who taps "Sign in with phone" on /login as
    // their first-ever action ends up here as a fresh signup, not a returning
    // user. Mirror the signup path's phoneNumber merge so the resulting
    // Firestore doc reflects verified:true; otherwise both the
    // PhoneVerifyBanner and the Settings → WhatsApp toggle stay stuck
    // (they both require phoneNumber.verified === true).
    await setDoc(
      doc(firestore, "users", signedInUser.uid),
      {
        phoneNumber: {
          prefix: phonePrefix,
          number: phoneNumber,
          verified: true,
        },
      },
      { merge: true },
    );

    if (created) {
      router.replace("/(auth)/onboarding" as any);
    }
  };

  const handleResendVerification = async (unverifiedUser: User) => {
    setModalInfo(null);
    setAuthLoading(true);
    try {
      await sendEmailVerification(unverifiedUser);
      setModalInfo({
        title: t("VERIFICATION_SENT"),
        message: t("VERIFICATION_EMAIL_SENT"),
        onClose: async () => {
          await signOut(auth);
        },
      });
    } catch (error) {
      const code = getErrorCode(error);
      // Resend has its own copy for too-many-requests ("wait before trying
      // again" — neutral, not login-attempt phrasing). Everything else routes
      // through the shared mapping so we never leak the raw Firebase message.
      const message =
        code === "auth/too-many-requests"
          ? t("RESEND_TOO_MANY_REQUESTS")
          : t(mapAuthErrorCode(code));
      setModalInfo({
        title: t("ERROR"),
        message,
        onClose: async () => {
          await signOut(auth);
        },
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const promptUnverifiedEmail = (unverifiedUser: User) => {
    setModalInfo({
      title: t("EMAIL_NOT_VERIFIED"),
      message: t("EMAIL_NOT_VERIFIED_RESEND_PROMPT"),
      secondaryAction: {
        text: t("CANCEL"),
        onPress: async () => {
          setModalInfo(null);
          await signOut(auth);
        },
      },
      primaryAction: {
        text: t("RESEND"),
        onPress: () => handleResendVerification(unverifiedUser),
      },
    });
  };

  const handleLogin = async ({ email, password }: LoginFormValues) => {
    setAuthLoading(true);
    try {
      let signedInUser: User | null = null;
      try {
        await signInWithEmailAndPassword(auth, email, password);
        signedInUser = auth.currentUser;
        await signedInUser?.reload();
      } catch (error) {
        setModalInfo({
          title: t("LOGIN_ERROR"),
          message: t(mapAuthErrorCode(getErrorCode(error))),
        });
        return;
      }

      if (!signedInUser?.emailVerified) {
        if (signedInUser) promptUnverifiedEmail(signedInUser);
        return;
      }

      try {
        const { created } = await ensureUserDocExists(firestore, signedInUser, {
          source: "login",
        });
        if (created) {
          router.replace("/(auth)/onboarding" as any);
        }
        // Existing user (created=false): InitialLayout routes them by their
        // existing doc state (onboarding vs /(tabs)/groups).
      } catch (error) {
        setModalInfo({
          title: t("ERROR"),
          message: t(mapAuthErrorCode(getErrorCode(error))),
        });
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const onGoogleButtonPress = async () => {
    setAuthLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      let signedInUser: User | null = null;

      try {
        try {
          const result = await signInWithPopup(auth, provider);
          signedInUser = result?.user ?? null;
        } catch (popupError: any) {
          const code = popupError?.code || popupError?.message || "";
          const errStr = String(popupError);
          const isCoopHint =
            /Cross-Origin-Opener-Policy/i.test(errStr) || /COOP/i.test(errStr);

          if (code.includes("auth/cancelled-popup-request")) {
            // Double-click on the Google button; the prior popup wins.
            return;
          }

          // COOP-affected browsers throw "popup-closed-by-user" even on
          // successful sign-in. Poll the auth state briefly before deciding.
          const settled = await waitForCurrentUser(1500);
          if (settled) {
            signedInUser = settled;
          } else if (
            code.includes("auth/popup-closed-by-user") &&
            !isCoopHint
          ) {
            // Genuine user cancellation.
            return;
          } else if (code.includes("auth/popup-blocked") || isCoopHint) {
            await signInWithRedirect(auth, provider);
            return;
          } else {
            throw popupError;
          }
        }
      } catch (error) {
        const code = getErrorCode(error);
        let title = t("GOOGLE_SIGN_IN_ERROR");
        let message: string;
        if (code === "auth/account-exists-with-different-credential") {
          title = t("ACCOUNT_EXISTS");
          message = t("ACCOUNT_EXISTS_MESSAGE");
        } else if (code === "auth/network-request-failed") {
          message = t("NETWORK_ERROR");
        } else if (code === "auth/too-many-requests") {
          message = t("TOO_MANY_LOGIN_ATTEMPTS");
        } else if (code === "auth/user-disabled") {
          message = t("ACCOUNT_DISABLED");
        } else {
          message = t("UNEXPECTED_ERROR");
        }
        setModalInfo({ title, message });
        return;
      }

      if (!signedInUser) return;

      // Google can succeed but leave the user at emailVerified: false — this
      // happens when the user previously linked an email/password credential
      // and hasn't clicked the verification link yet. InitialLayout doesn't
      // redirect in that state, so without this check the popup closes and
      // nothing visible happens. Show the same resend prompt as email/pw.
      await signedInUser.reload();
      if (!signedInUser.emailVerified) {
        promptUnverifiedEmail(signedInUser);
        return;
      }

      try {
        const { created } = await ensureUserDocExists(firestore, signedInUser, {
          source: "login",
        });
        if (created) {
          router.replace("/(auth)/onboarding" as any);
        }
        // Existing user (created=false): InitialLayout routes them based on
        // their existing onboarding state.
      } catch (error) {
        const key = mapAuthErrorCode(getErrorCode(error));
        if (key === "NETWORK_ERROR") {
          // Sign-in already succeeded; the first Firestore read after auth
          // can race the token-propagation window. _layout.web.tsx's
          // onSnapshot heals and routes once the channel is ready — don't
          // flash a confusing error at a user who is actually signed in.
          console.warn(
            "ensureUserDocExists failed with network error; layout will handle redirect:",
            error,
          );
          return;
        }
        setModalInfo({ title: t("ERROR"), message: t(key) });
      }
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <SafeAreaView
      style={[
        styles.safeAreaViewContainer,
        { backgroundColor: isLargeScreen ? "#1e3a8a" : "#ffffff" },
      ]}
      edges={["top", "bottom"]}
    >
      <Head>
        <title>{t("PAGE_TITLE_LOGIN")}</title>
      </Head>
      <StatusBar style={isLargeScreen ? "light" : "dark"} animated={true} />
      <FormPageContainer>
        <FormCard>
          <View className="items-center mb-6">
            <Image
              source={require("../../assets/images/vehilink-icon.webp")}
              style={{ width: 100, height: 100, borderRadius: 50 }}
              contentFit="cover"
              accessibilityLabel={t("VEHILINK_LOGO_ALT")}
            />
            <ThemedText className="mt-4 text-xl font-normal text-gray-900">
              {t("LOGIN")}
            </ThemedText>
          </View>

          {!phoneFlowActive && (
            <>
              <SocialSignInRow
                onGooglePress={onGoogleButtonPress}
                onPhonePress={() => setPhoneFlowActive(true)}
                disabled={authLoading}
                phoneLabelKey="SIGN_IN_WITH_PHONE"
              />

              <AuthTermsNotice introKey="BY_CONTINUING_AGREE" />

              <OrDivider />

              <RHFFormInput
                control={control}
                name="email"
                label={t("EMAIL")}
                placeholder={t("EMAIL_PLACEHOLDER")}
                autoCapitalize="none"
                keyboardType="email-address"
              />

              <RHFFormInput
                control={control}
                name="password"
                label={t("PASSWORD")}
                password
                placeholder={t("PASSWORD_PLACEHOLDER")}
              />

              <View className="items-end mb-6">
                <FormLinkButton
                  text={t("FORGOT_PASSWORD")}
                  onPress={() => router.push("/(auth)/forgot-password")}
                />
              </View>

              <FormButton
                onPress={handleSubmit(handleLogin)}
                disabled={!hasRequiredFields || authLoading}
                text={t("LOGIN")}
              />
            </>
          )}

          {phoneFlowActive && (
            <PhoneAuthFlow
              onSuccess={handlePhoneSignInConfirm}
              onCancel={() => setPhoneFlowActive(false)}
              onSendError={handlePhoneSignInSendError}
              subtitleKey="PHONE_SIGNIN_SUBTITLE"
              sendCodeKey="PHONE_SIGNUP_SEND_CODE"
              loading={authLoading}
              onLoadingChange={setAuthLoading}
              footer={<AuthTermsNotice introKey="BY_CONTINUING_AGREE" />}
            />
          )}

          <AuthSwitchLink
            promptKey="DONT_HAVE_ACCOUNT"
            actionKey="SIGN_UP"
            targetHref="/(auth)/signup"
          />
        </FormCard>
      </FormPageContainer>
      {authLoading && !phoneFlowActive && (
        // Overlay the spinner instead of swapping the FormCard subtree. The
        // ternary used to live here unmounted PhoneAuthFlow mid-await, losing
        // its step/phoneConfirmation state when loading flipped back to false.
        // The phone flow now shows a button-level spinner and keeps its own
        // state alive via the module-level stash, so it doesn't need the
        // overlay at all.
        <View style={StyleSheet.absoluteFillObject} pointerEvents="auto">
          <FormPageContainer>
            <ActivityIndicator size="large" color="#ffffff" />
          </FormPageContainer>
        </View>
      )}
      <AlertModal
        visible={!!modalInfo}
        title={modalInfo?.title ?? ""}
        message={modalInfo?.message ?? ""}
        onClose={() => {
          modalInfo?.onClose?.();
          setModalInfo(null);
        }}
        primaryAction={modalInfo?.primaryAction}
        secondaryAction={modalInfo?.secondaryAction}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeAreaViewContainer: { flex: 1 },
});

export default LoginScreen;
